import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, ChevronLeft, ChevronRight, Coffee, Play, ShoppingBag } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const EXPLORE_TILES = [
  {
    title: "WATCH",
    copy: "Watch Poolside Cooking",
    to: "/watch",
    icon: Play,
    testId: "explore-watch",
  },
  {
    title: "CAFÉ",
    copy: "Visit the Poolside Cooking Café",
    to: "/cafe",
    icon: Coffee,
    testId: "explore-cafe",
  },
  {
    title: "EVENTS",
    copy: "Explore Poolside events",
    to: "/events",
    icon: CalendarDays,
    testId: "explore-events",
  },
  {
    title: "SHOP",
    copy: "Shop Poolside merchandise",
    to: "/shop",
    icon: ShoppingBag,
    testId: "explore-shop",
  },
];

export default function Home() {
  const [platforms, setPlatforms] = useState([]);
  const [guests, setGuests] = useState([]);
  const guestsRowRef = useRef(null);
  const scrollGuests = (dir) => guestsRowRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });

  useEffect(() => {
    api.get("/content/platforms").then((r) => setPlatforms(r.data)).catch(() => {});
    api.get("/content/guests").then((r) => setGuests(r.data)).catch(() => {});
  }, []);

  const featuredGuests = guests.filter((g) => g.photo && !g.photo.includes("event-")).slice(0, 8);

  return (
    <div data-testid="home-page">
      {/* HERO — welcome to Poolside Cooking */}
      <section className="relative overflow-hidden lg:min-h-[72vh] lg:flex lg:items-center" data-testid="hero-section">
        <img src="/assets/shop-hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" data-testid="hero-image" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.78),rgba(255,255,255,0.28)_55%,rgba(255,255,255,0.05)_78%)] pointer-events-none" aria-hidden="true" />
        <div className="relative container-site w-full py-16 sm:py-20 lg:py-20 text-center max-w-3xl mx-auto">
          <motion.img
            src="/logo.png"
            alt="Poolside Cooking"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="h-14 sm:h-[70px] lg:h-[86px] w-auto mx-auto"
            data-testid="hero-logo"
          />
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.6 }}
            className="mt-7 flex items-center justify-center gap-4"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-ink/50" aria-hidden="true" />
            <p className="text-[10px] sm:text-xs tracking-[0.35em] uppercase font-bold text-ink">Welcome to Poolside Cooking</p>
            <span className="h-px w-10 bg-ink/50" aria-hidden="true" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.24, duration: 0.6 }}
            className="mt-6 font-hero font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-ink text-balance"
            data-testid="hero-headline"
          >
            GOOD FOOD. GREAT VIBES.
            <br className="hidden sm:block" />
            <span className="text-pool">REAL CONVERSATIONS.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.36, duration: 0.6 }}
            className="mt-5 text-ink/70 leading-relaxed text-sm sm:text-base font-medium max-w-xl mx-auto"
            data-testid="hero-subcopy"
          >
            Join Ashley Marie for delicious food, special guests, and feel-good conversations.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.48, duration: 0.6 }}
            className="mt-8"
          >
            <Link
              to="/watch"
              data-testid="hero-watch-cta"
              className="inline-flex items-center justify-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-[11px] sm:text-xs tracking-[0.22em] uppercase px-7 py-3.5 rounded-full shadow-[0_12px_34px_rgba(2,150,180,0.25)] transition-all hover:-translate-y-0.5"
            >
              Explore the Show <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* WATCH POOLSIDE — now showing */}
      {platforms.length > 0 && (
        <section className="bg-[#F3FBFC]" data-testid="watch-section">
          <div className="container-site py-10 sm:py-12 text-center">
            <Reveal>
              <p className="label-eyebrow mb-3 justify-center">Now Showing</p>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-ink" data-testid="watch-headline">
                Watch Poolside <span className="text-pool">Cooking.</span>
              </h2>
              <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed max-w-xl mx-auto" data-testid="watch-subcopy">
                Good food. Great guests. Real conversations — wherever you watch.
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto" data-testid="watch-platforms">
              {platforms.map((p, i) => {
                const logoSize =
                  p.name === "WGNT 27"
                    ? "h-9 sm:h-10"
                    : p.name === "Your Channel Network"
                      ? "h-16 sm:h-[76px]"
                      : "h-11 sm:h-12";
                const card = (
                  <>
                    <div className="flex-1 flex items-center justify-center px-4 h-[96px]">
                      <img
                        src={imgSrc(p.logo)}
                        alt={p.name}
                        title={p.name}
                        loading="lazy"
                        className={`${logoSize} w-auto max-w-full object-contain transition-transform duration-300 group-hover:scale-105`}
                      />
                    </div>
                    {p.detail && (
                      <p className="px-3 min-h-[40px] flex items-center justify-center text-center text-[9px] tracking-[0.16em] uppercase font-bold text-slate-400 group-hover:text-pool-deep transition-colors leading-snug">
                        {p.detail}
                      </p>
                    )}
                  </>
                );
                const cls =
                  "group flex flex-col items-stretch h-full bg-white border border-slate-200/70 rounded-xl transition-all duration-300 hover:-translate-y-0.5 hover:border-pool/50";
                return (
                  <Reveal key={p.id} delay={i * 0.06} className="h-full">
                    {p.link ? (
                      <a href={p.link} target="_blank" rel="noreferrer" className={cls} data-testid={`watch-platform-${p.id}`}>
                        {card}
                      </a>
                    ) : (
                      <div className={cls} data-testid={`watch-platform-${p.id}`}>
                        {card}
                      </div>
                    )}
                  </Reveal>
                );
              })}
            </div>
            <Reveal className="mt-8">
              <Link
                to="/watch"
                data-testid="watch-poolside-cta"
                className="inline-flex items-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-xs tracking-[0.18em] uppercase px-8 py-3.5 rounded-full transition-colors"
              >
                Watch Now <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* MEET YOUR HOST */}
      <section className="relative overflow-hidden bg-[#F0FAFC] py-16 sm:py-20" data-testid="host-section">
        <span className="pointer-events-none select-none absolute -top-10 right-0 font-script text-[18vw] leading-none text-pool/[0.07]" aria-hidden="true">Ashley</span>
        <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-pool-light/40 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -top-16 left-1/3 h-56 w-56 rounded-full bg-amber-200/30 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative container-site grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative max-w-sm mx-auto lg:mx-0">
              <div className="absolute -inset-3 rounded-3xl bg-pool/15 -translate-x-3 translate-y-3" aria-hidden="true" />
              <div className="relative bg-white p-2.5 rounded-3xl shadow-[0_24px_60px_rgba(2,60,80,0.16)]">
                <div className="relative aspect-[3/4] overflow-hidden rounded-2xl">
                  <img src="/assets/ashley-host-poolside.jpg" alt="Ashley Marie, host of Poolside Cooking" loading="lazy" className="img-zoom h-full w-full object-cover object-[center_20%]" data-testid="host-image" />
                </div>
                <span className="absolute -bottom-4 left-5 bg-ink text-white text-[9px] sm:text-[10px] tracking-[0.25em] uppercase font-bold px-4 py-2 rounded-full shadow-lg -rotate-2" data-testid="host-badge">
                  Host &amp; Co-Founder
                </span>
              </div>
              <p className="absolute -top-6 -right-2 sm:-right-6 font-script text-2xl sm:text-3xl text-pool rotate-6" aria-hidden="true">Pull up a chair.</p>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.12}>
            <p className="label-eyebrow mb-5">Meet Your Host</p>
            <h2 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.0] text-ink" data-testid="host-headline">
              Hey, I&rsquo;m <span className="text-pool">Ashley Marie.</span>
            </h2>
            <div className="mt-7 border-l-4 border-pool pl-6 max-w-xl" data-testid="host-statement">
              <p className="font-hero text-xl sm:text-2xl text-ink/85 leading-snug">
                &ldquo;Good food may bring us together, but the conversations are what make Poolside special.&rdquo;
              </p>
            </div>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-xl text-sm sm:text-base">
              As host of Poolside Cooking, Ashley Marie brings together celebrity guests, incredible food and genuine
              conversation — all with the energy and atmosphere that makes Poolside feel like home.
            </p>
            <p className="mt-6 font-script text-3xl sm:text-4xl text-pool" aria-hidden="true">— Ashley Marie</p>
            <Link
              to="/about"
              data-testid="meet-ashley-cta"
              className="mt-8 inline-flex items-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full shadow-[0_12px_34px_rgba(2,150,180,0.25)] transition-all hover:-translate-y-0.5"
            >
              Meet Ashley <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FACES AT THE POOL — featured guests carousel */}
      {featuredGuests.length > 0 && (
        <section className="py-14 sm:py-16 bg-cream" data-testid="spotlight-section">
          <div className="container-site">
            <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-9">
              <div className="max-w-xl">
                <p className="label-eyebrow mb-4">Celebrity Guests</p>
                <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-[2.6rem] tracking-tight leading-[1.02] text-ink" data-testid="spotlight-preview-headline">
                  Faces at <span className="text-pool">the Pool.</span>
                </h2>
                <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">
                  Great food brings people together. Great conversation keeps them at the table.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="hidden sm:flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => scrollGuests(-1)}
                    aria-label="Scroll guests left"
                    data-testid="guests-scroll-left"
                    className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-500 hover:border-pool hover:text-pool flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollGuests(1)}
                    aria-label="Scroll guests right"
                    data-testid="guests-scroll-right"
                    className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-500 hover:border-pool hover:text-pool flex items-center justify-center transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <Link
                  to="/spotlight"
                  data-testid="view-all-guests"
                  className="inline-flex items-center gap-3 bg-ink hover:bg-pool-dark text-white font-bold text-xs tracking-[0.15em] uppercase px-7 py-3.5 rounded-full transition-colors"
                >
                  Meet Our Guests <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
            <div
              ref={guestsRowRef}
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="spotlight-carousel"
            >
              {featuredGuests.map((g) => (
                <Link
                  key={g.id}
                  to={`/spotlight/${g.id}`}
                  className="group relative flex-none w-[58%] sm:w-[31%] lg:w-[18.5%] snap-start overflow-hidden rounded-2xl bg-cyan-950"
                  data-testid={`spotlight-guest-${g.id}`}
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={imgSrc(g.photo)}
                      alt={g.name}
                      loading="lazy"
                      style={{ objectPosition: g.focus || "center 25%" }}
                      className="img-zoom-soft h-full w-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink/75 via-ink/25 to-transparent pointer-events-none" />
                  <div className="absolute bottom-0 inset-x-0 p-4">
                    <p className="font-display font-bold text-white text-sm sm:text-base leading-tight">{g.name}</p>
                    {g.category && (
                      <p className="text-pool-light text-[9px] mt-1 tracking-[0.22em] uppercase font-bold">{g.category}</p>
                    )}
                    <p
                      className="mt-2 inline-flex items-center gap-1.5 text-white text-[10px] tracking-[0.22em] uppercase font-bold opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300"
                      data-testid={`view-guest-${g.id}`}
                    >
                      View Guest <ArrowRight className="h-3 w-3" />
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SIGNATURE BRAND MOMENT */}
      <section className="bg-white py-14 sm:py-16" data-testid="signature-section">
        <div className="container-site text-center">
          <Reveal>
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase font-bold text-slate-400" data-testid="signature-lead">
              Around here, there&rsquo;s always one question…
            </p>
            <p className="mt-3 font-script text-5xl sm:text-6xl lg:text-7xl text-pool" data-testid="signature-phrase">
              Chef, Where You At?
            </p>
          </Reveal>
        </div>
      </section>

      {/* BE ON THE SHOW — evergreen promo */}
      <section className="water-panel text-white noise-overlay py-16 sm:py-20" data-testid="be-on-show-section">
        <img src="/assets/water.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20 pointer-events-none" />
        <div className="relative container-site grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <p className="label-eyebrow mb-4">Be on the Show</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.0]" data-testid="be-on-show-headline">
              Want to Pull Up<br />to <span className="text-pool-light">the Pool?</span>
            </h2>
            <p className="mt-5 text-white/80 text-sm sm:text-base leading-relaxed max-w-md">
              We&rsquo;re always looking for great personalities, unforgettable stories, talented chefs and people
              doing amazing things in their communities.
            </p>
            <Link
              to="/be-on-the-show"
              data-testid="be-on-show-cta"
              className="mt-8 inline-flex items-center gap-3 bg-white text-ink hover:bg-pool-light font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
            >
              Be on the Show <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[4/3] overflow-hidden rounded-xl translate-y-4 shadow-xl">
                <img src="/assets/beonshow-a.jpg" alt="Guests cooking and talking on the Poolside Cooking set" loading="lazy" className="img-zoom h-full w-full object-cover" />
              </div>
              <div className="aspect-[4/3] overflow-hidden rounded-xl shadow-xl">
                <img src="/assets/beonshow-b.jpg" alt="Behind the scenes — filming Poolside Cooking on set" loading="lazy" className="img-zoom h-full w-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXPLORE POOLSIDE — quick discovery nav */}
      <section className="relative bg-white py-10 sm:py-14 overflow-hidden" data-testid="explore-section">
        <div className="absolute -top-10 -right-16 h-56 w-56 rounded-full bg-pool-light/40 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative container-site">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-7">
            <div>
              <p className="label-eyebrow mb-3">Discover More</p>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-ink" data-testid="explore-headline">
                Explore <span className="text-pool">Poolside</span><span className="text-amber-400">.</span>
              </h2>
            </div>
            <p className="font-script text-xl sm:text-2xl text-pool/80 -rotate-1" data-testid="explore-tagline">
              There&rsquo;s more waiting by the pool.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="explore-grid">
            {EXPLORE_TILES.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.06} className="h-full">
                <Link to={t.to} className="group block h-full" data-testid={t.testId}>
                  <div className="h-full bg-white/70 backdrop-blur-md border border-pool/20 rounded-2xl px-5 py-6 sm:px-6 sm:py-7 flex flex-col items-start shadow-[0_8px_28px_rgba(2,60,80,0.06)] transition-all duration-300 group-hover:-translate-y-1 group-hover:border-pool group-hover:shadow-[0_18px_44px_rgba(2,150,180,0.18)]">
                    <span className="h-10 w-10 rounded-full bg-pool/10 border border-pool/25 flex items-center justify-center text-pool mb-4">
                      <t.icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <p className="font-display font-black text-ink text-sm sm:text-base tracking-[0.08em]">{t.title}</p>
                    <p className="mt-1.5 text-xs sm:text-sm text-slate-500 leading-relaxed">{t.copy}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-pool">
                      Explore <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SHOP — teaser collage */}
      <section className="py-10 sm:py-12 lg:py-14 bg-cream relative overflow-hidden" data-testid="cookbook-section">
        <div className="container-site grid lg:grid-cols-12 gap-10 lg:gap-8 items-center relative">
          <Reveal className="lg:col-span-5">
            <p className="label-eyebrow mb-4">From Our Kitchen to Yours</p>
            <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 leading-[1.05]" data-testid="cookbook-headline">
              Bring <span className="text-pool">Poolside Home.</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-md text-sm sm:text-base">
              Shop the official cookbook and everyday favorites inspired by Poolside Cooking.
            </p>
            <div className="mt-6">
              <Link
                to="/shop"
                data-testid="shop-poolside-cta"
                className="inline-flex items-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-3.5 rounded-full transition-colors"
              >
                Shop Poolside <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.12}>
            <div className="relative grid grid-cols-2 gap-3 lg:gap-0 lg:block lg:h-[360px]" data-testid="shop-teaser-collage">
              <Link
                to="/shop"
                data-testid="cookbook-hero-card"
                className="col-span-2 max-w-[240px] mx-auto lg:max-w-none lg:mx-0 lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2 lg:w-[30%] z-20 block -rotate-2 transition-all duration-500 hover:rotate-0 hover:-translate-y-2"
              >
                <img
                  src="/assets/shop-cookbook.jpg"
                  alt="The Poolside Cooking Cookbook by Jemmalyn Hewlett"
                  loading="lazy"
                  className="w-full rounded-2xl shadow-[0_20px_44px_rgba(2,20,30,0.18)]"
                  data-testid="cookbook-cover"
                />
              </Link>
              {[
                { src: "/assets/shop-tee-back.jpg", label: "Chef, Where You At? Tee", slug: "tee", cls: "lg:absolute lg:right-[4%] lg:top-0 lg:w-[25%] rotate-2" },
                { src: "/assets/shop-tumbler.jpg", label: "Poolside Insulated Tumbler", slug: "tumbler", cls: "lg:absolute lg:right-[27%] lg:bottom-0 lg:w-[22%] -rotate-1 z-10" },
                { src: "/assets/shop-cap.jpg", label: "Poolside Logo Cap", slug: "cap", cls: "lg:absolute lg:right-0 lg:bottom-[6%] lg:w-[24%] rotate-3" },
              ].map((m) => (
                <Link
                  key={m.slug}
                  to="/shop"
                  data-testid={`merch-card-${m.slug}`}
                  className={`${m.cls} block transition-all duration-500 hover:-translate-y-2 hover:scale-[1.03]`}
                >
                  <div className="bg-white p-2 rounded-xl shadow-[0_14px_36px_rgba(2,20,30,0.12)] hover:shadow-[0_22px_50px_rgba(2,20,30,0.2)] transition-shadow duration-500">
                    <div className="aspect-square overflow-hidden rounded-lg bg-[#f4f1ec]">
                      <img src={m.src} alt={m.label} loading="lazy" className="h-full w-full object-cover" />
                    </div>
                  </div>
                </Link>
              ))}
              <div className="col-span-2 lg:col-span-0 lg:absolute lg:right-[3%] lg:top-[42%] z-30 flex lg:flex-col items-center lg:items-end gap-3 lg:gap-2" data-testid="collection-label">
                <span className="bg-ink text-white text-[9px] tracking-[0.25em] uppercase font-bold px-4 py-1.5 rounded-full shadow-md">Coming Soon</span>
                <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">The Poolside Collection</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

    </div>
  );
}
